/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} LowestPriceRefs
 * @property {HTMLElement} [lowestPrice]
 */

/**
 * Keeps the lowest-price line in step with the selected variant.
 *
 * Deliberately does no arithmetic. The figure depends on the variant's own metafield and price,
 * and on a variant change the theme re-renders the section server-side, so the incoming HTML
 * already carries the line for the new variant. Swapping it in is the only way to keep the
 * figure derived from one piece of code, in the block's Liquid.
 *
 * Mirrors member-price.js — same event, same block-id matching.
 *
 * @extends {Component<LowestPriceRefs>}
 */
class LowestPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Replaces the rendered line when a different variant is selected.
   * @param {any} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newLowestPrice = html.querySelector(`lowest-price[data-block-id="${this.dataset.blockId}"]`);
        const newRow = newLowestPrice?.querySelector('[ref="lowestPrice"]');

        // The new variant may have nothing to show, which differs from variant to variant.
        // Removing the old line is the right response: leaving it up would state another
        // variant's lowest price next to this one's.
        if (!newRow) {
          this.refs.lowestPrice?.remove();
          return;
        }

        if (this.refs.lowestPrice) {
          this.refs.lowestPrice.replaceWith(newRow);
        } else {
          this.append(newRow);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lowest-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('lowest-price')) {
  customElements.define('lowest-price', LowestPrice);
}
